import { useContext, useState, useCallback, useEffect } from 'react';
import { diContainerContext } from 'application/contexts/useDiContainer';
import { supportUserContext } from 'application/contexts/useSupportUser';
import { useLoading } from 'application/contexts/useLoading';
import { InviteUserRepository } from 'interfaceAdapter/repositories/InviteUser';
import { ImportUsersCsvRepository } from 'interfaceAdapter/repositories/ImportUsersCsv';
import { GetRegisteredCompaniesQuery } from 'interfaceAdapter/queries/GetRegisteredCompanies';
import { SupporterChatRoomQuery } from 'interfaceAdapter/queries/SupporterChatRoom';

export const useSupportUserDashboard = () => {
    const diContainer = useContext(diContainerContext);
    const { loading, setLoading } = useLoading();
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const { user } = useContext(supportUserContext);

    const inviteUserRepository = diContainer.resolve(InviteUserRepository);
    const importUsersCsvRepository = diContainer.resolve(ImportUsersCsvRepository);
    const getRegisteredCompaniesQuery = diContainer.resolve(GetRegisteredCompaniesQuery);
    const supporterChatRoomQuery = diContainer.resolve(SupporterChatRoomQuery);

    const [invitedEmails, setInvitedEmails] = useState<
        Array<{
            id: number;
            email: string;
            type: 'seller' | 'buyer';
            invitedAt: string;
            status: string;
        }>
    >([]);

    const [registeredCompanies, setRegisteredCompanies] = useState<
        Array<{
            id: number;
            name: string;
            type: 'seller' | 'buyer';
            registeredAt: string;
            email: string;
            profile_rate: number;
            offer_count: number;
            match_count: number;
        }>
    >([]);

    const refreshRegisteredCompanies = useCallback(async () => {
        try {
            const data = await getRegisteredCompaniesQuery.fetch();
            // 登録済み企業一覧をマッピング
            const mappedRegisteredData = data.registered.map((item) => ({
                id: item.id,
                name: item.name,
                type: item.type,
                registeredAt: item.registered_at || '',
                email: item.email,
                profile_rate: item.profile_rate,
                offer_count: item.offer_count,
                match_count: item.match_count,
            }));
            setRegisteredCompanies(mappedRegisteredData);

            // 招待中のメールアドレス一覧をマッピング
            const mappedInvitedData = data.invited.map((item) => ({
                id: item.id,
                email: item.email,
                type: item.type,
                invitedAt: item.invited_at || '',
                status: item.status,
            }));
            setInvitedEmails(mappedInvitedData);
        } catch (error) {
            console.error('企業一覧の取得に失敗しました:', error);
        }
    }, []);

    // 登録済み企業一覧を取得
    useEffect(() => {
        refreshRegisteredCompanies();
    }, [refreshRegisteredCompanies]);

    const inviteUser = useCallback(
        async (email: string, invitationType: 'seller' | 'buyer') => {
            setLoading(true);
            try {
                await inviteUserRepository.post(email, invitationType);
                setSnackbarMessage('招待メールを送信しました');
                setShowSnackbar(true);

                // 招待中のメールアドレス一覧を再取得
                await refreshRegisteredCompanies();
            } catch (error) {
                console.error('招待メール送信エラー:', error);
                setSnackbarMessage(error instanceof Error ? error.message : '招待メールの送信に失敗しました');
                setShowSnackbar(true);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [inviteUserRepository, setLoading, refreshRegisteredCompanies],
    );

    const importUsersCsv = useCallback(
        async (csvFile: File, invitationType: 'seller' | 'buyer') => {
            setLoading(true);
            try {
                const result = await importUsersCsvRepository.post(csvFile, invitationType);
                const successCount = result?.success || 0;
                const totalCount = result?.total || 0;
                setSnackbarMessage(`CSVファイルから${successCount}件の招待メールを送信しました（全${totalCount}件中）`);
                setShowSnackbar(true);

                // 招待中のメールアドレス一覧を再取得
                await refreshRegisteredCompanies();
            } catch (error) {
                console.error('CSVインポートエラー:', error);
                setSnackbarMessage(error instanceof Error ? error.message : 'CSVファイルのインポートに失敗しました');
                setShowSnackbar(true);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [importUsersCsvRepository, setLoading, refreshRegisteredCompanies],
    );

    const findChatRoomId = useCallback(
        async (companyId: number, type: 'seller' | 'buyer'): Promise<number | null> => {
            try {
                const chatRooms = await supporterChatRoomQuery.fetchRoomList();
                const chatRoom = chatRooms.find((room) => {
                    if (type === 'seller') {
                        return room.company?.id === companyId;
                    } else {
                        return room.buyer?.id === companyId;
                    }
                });
                return chatRoom?.id || null;
            } catch (error) {
                console.error('チャットルームの取得に失敗しました:', error);
                return null;
            }
        },
        [supporterChatRoomQuery],
    );

    return {
        loading,
        showSnackbar,
        setShowSnackbar,
        snackbarMessage,
        invitedEmails,
        registeredCompanies,
        inviteUser,
        importUsersCsv,
        refreshRegisteredCompanies,
        findChatRoomId,
    };
};
